import { cn } from '@brew/ui/cn'
import { Fragment, type MouseEvent, type ReactNode } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PAGE_TITLE_CLASSES } from '@/lib/shared/layout-constants'

export type DetailPageBreadcrumbProps = {
  parentLabel: string
  parentHref: string
  trail?: ReadonlyArray<{ label: string; href: string }>
  title: string
  /** Replaces the static current-page crumb (see `DetailPageHeader`). */
  titleSlot?: ReactNode
  /**
   * A host without these routes (the product demo) takes every crumb
   * press itself; its links then never prefetch.
   */
  onLinkClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

/**
 * The app header's `Parent / … / Child` crumb: the parent and trail are
 * plain links, the current page reads in the page-title type. Rendered by
 * `DetailPageHeader` into the app header, and by the product demo's header.
 */
export function DetailPageBreadcrumb({
  parentLabel,
  parentHref,
  trail,
  title,
  titleSlot,
  onLinkClick,
}: DetailPageBreadcrumbProps) {
  const linkProps = onLinkClick
    ? { onClick: onLinkClick, prefetch: false }
    : undefined
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="min-w-0 flex-nowrap gap-1.5">
        <BreadcrumbItem className="shrink-0">
          <BreadcrumbLink
            href={parentHref}
            className="font-medium"
            {...linkProps}
          >
            {parentLabel}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {trail?.map((crumb) => (
          <Fragment key={crumb.href}>
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbLink
                href={crumb.href}
                className="truncate"
                {...linkProps}
              >
                {crumb.label}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </Fragment>
        ))}
        <BreadcrumbItem className="min-w-0">
          {titleSlot ?? (
            <BreadcrumbPage className={cn(PAGE_TITLE_CLASSES, 'min-w-0')}>
              {title}
            </BreadcrumbPage>
          )}
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}
